import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Button, Switch, Chip, IconButton,
    Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Alert, Tooltip, Divider, Avatar, InputAdornment, CircularProgress,
} from '@mui/material';
import {
    Campaign, Business, TrendingUp, History, Settings, Add, Edit, Delete,
    Pause, PlayArrow, Visibility, Search, Download, CheckCircle, Cancel,
    Public, PhotoLibrary, Explore, Map, Attractions,
} from '@mui/icons-material';
import {
    AdPartner, AdCampaign, AdSettings, AdAuditLog, AdPage, AdTier,
    MOCK_PARTNERS, MOCK_CAMPAIGNS, MOCK_AUDIT_LOG, DEFAULT_ROTATION_INTERVALS,
    getAdStats, getPartnerById,
} from '../data/mockAds';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { api } from '../utils/api';

type TabValue = 'overview' | 'campaigns' | 'partners' | 'analytics' | 'audit';

const pageIcons: Record<AdPage, React.ReactElement> = {
    community: <Public sx={{ fontSize: 16 }} />,
    my_trips: <Explore sx={{ fontSize: 16 }} />,
    trip_details: <Map sx={{ fontSize: 16 }} />,
    my_photos: <PhotoLibrary sx={{ fontSize: 16 }} />,
    destinations: <Attractions sx={{ fontSize: 16 }} />,
};

const pageLabels: Record<AdPage, string> = {
    community: 'Community', my_trips: 'My Trips', trip_details: 'Trip Details',
    my_photos: 'My Photos', destinations: 'Destinations',
};

const tierColors: Record<AdTier, string> = {
    premium: '#FFD700', standard: '#90CAF9', featured: '#FF6B35',
};

const AdminAdsPage: React.FC = () => {
    const { isEnabled, refetchFlags } = useFeatureFlags();
    const globalAdsEnabled = isEnabled('advertisements_global');
    
    const [activeTab, setActiveTab] = useState<TabValue>('overview');
    const [settings, setSettings] = useState<AdSettings>(() => ({
        globalEnabled: false,
        pageSettings: { community: false, my_trips: false, trip_details: false, my_photos: false, destinations: false },
        tierSettings: { standard: false, premium: false, featured: false },
        rotationIntervals: DEFAULT_ROTATION_INTERVALS,
        maxImpressionsPerAdPerSession: 3,
        delayBeforeFirstAd: 3,
    }));
    const [flagsLoading, setFlagsLoading] = useState(true);
    const [flagsError, setFlagsError] = useState<string | null>(null);
    const [campaigns, setCampaigns] = useState<AdCampaign[]>(MOCK_CAMPAIGNS);
    const [partners, setPartners] = useState<AdPartner[]>(MOCK_PARTNERS);
    const [auditLog] = useState<AdAuditLog[]>(MOCK_AUDIT_LOG);
    const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
    const [partnerDialogOpen, setPartnerDialogOpen] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState<AdCampaign | null>(null);
    const [editingPartner, setEditingPartner] = useState<AdPartner | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    
    const stats = useMemo(() => getAdStats(), []);
    
    // Fetch ad flags from backend on mount. Mirrors SystemManagementTab pattern (#153).
    useEffect(() => {
        const fetchAdFlags = async () => {
            setFlagsLoading(true);
            setFlagsError(null);
            try {
                const response = await api.get('/admin/feature-flags');
                const data = await response.json();
                if (!data.success) {
                    setFlagsError('Failed to load advertisement settings');
                    return;
                }
                const adFlags = (data.flags as Array<{ flag_key: string; enabled: boolean }>)
                    .filter(f => f.flag_key.startsWith('advertisements_'));
                const nextPage: Record<AdPage, boolean> = { community: false, my_trips: false, trip_details: false, my_photos: false, destinations: false };
                const nextTier: Record<AdTier, boolean> = { standard: false, premium: false, featured: false };
                let nextGlobal = false;
                for (const flag of adFlags) {
                    const suffix = flag.flag_key.replace('advertisements_', '');
                    if (suffix === 'global') {
                        nextGlobal = flag.enabled;
                    } else if (suffix in nextPage) {
                        (nextPage as Record<string, boolean>)[suffix] = flag.enabled;
                    } else if (suffix in nextTier) {
                        (nextTier as Record<string, boolean>)[suffix] = flag.enabled;
                    }
                }
                setSettings(prev => ({ ...prev, globalEnabled: nextGlobal, pageSettings: nextPage, tierSettings: nextTier }));
            } catch (err) {
                console.error('Error fetching ad flags:', err);
                setFlagsError('Failed to load advertisement settings');
            } finally {
                setFlagsLoading(false);
            }
        };
        fetchAdFlags();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const togglePageSetting = async (page: AdPage) => {
        const newValue = !settings.pageSettings[page];
        const flagKey = `advertisements_${page}`;
        // Optimistic update
        setSettings(prev => ({ ...prev, pageSettings: { ...prev.pageSettings, [page]: newValue } }));
        try {
            const response = await api.put(`/admin/feature-flags/${flagKey}`, { enabled: newValue });
            const data = await response.json();
            if (!data.success) {
                // Roll back
                setSettings(prev => ({ ...prev, pageSettings: { ...prev.pageSettings, [page]: !newValue } }));
                setFlagsError(`Failed to update ${flagKey}`);
                return;
            }
            refetchFlags();
        } catch (err) {
            console.error('Error updating page flag:', err);
            setSettings(prev => ({ ...prev, pageSettings: { ...prev.pageSettings, [page]: !newValue } }));
            setFlagsError(`Failed to update ${flagKey}`);
        }
    };
    const toggleTierSetting = async (tier: AdTier) => {
        const newValue = !settings.tierSettings[tier];
        const flagKey = `advertisements_${tier}`;
        // Optimistic update
        setSettings(prev => ({ ...prev, tierSettings: { ...prev.tierSettings, [tier]: newValue } }));
        try {
            const response = await api.put(`/admin/feature-flags/${flagKey}`, { enabled: newValue });
            const data = await response.json();
            if (!data.success) {
                // Roll back
                setSettings(prev => ({ ...prev, tierSettings: { ...prev.tierSettings, [tier]: !newValue } }));
                setFlagsError(`Failed to update ${flagKey}`);
                return;
            }
            refetchFlags();
        } catch (err) {
            console.error('Error updating tier flag:', err);
            setSettings(prev => ({ ...prev, tierSettings: { ...prev.tierSettings, [tier]: !newValue } }));
            setFlagsError(`Failed to update ${flagKey}`);
        }
    };
    
    const toggleCampaignStatus = (campaignId: string) => {
        setCampaigns(prev => prev.map(c => 
            c.id === campaignId ? { ...c, status: c.status === 'active' ? 'paused' : 'active' } : c
        ));
    };
    
    const togglePartnerStatus = (partnerId: string) => {
        setPartners(prev => prev.map(p => 
            p.id === partnerId ? { ...p, status: p.status === 'active' ? 'paused' : 'active' } : p
        ));
    };
    
    const filteredCampaigns = useMemo(() => {
        if (!searchQuery) return campaigns;
        const q = searchQuery.toLowerCase();
        return campaigns.filter(c => 
            c.name.toLowerCase().includes(q) ||
            getPartnerById(c.partnerId)?.name.toLowerCase().includes(q)
        );
    }, [campaigns, searchQuery]);

    // Overview Tab
    const renderOverview = () => (
        <Box>
            {flagsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}
            {flagsError && (
                <Alert severity="error" onClose={() => setFlagsError(null)} sx={{ mb: 2 }}>
                    {flagsError}
                </Alert>
            )}
            {!globalAdsEnabled && (
                <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Advertisements are disabled globally</Typography>
                    <Typography variant="body2">Enable in System Settings to allow ads.</Typography>
                </Alert>
            )}
            
            {/* Stats */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {[
                    { label: 'Active Campaigns', value: stats.activeCampaigns, sub: `of ${campaigns.length} total`, color: '#2D6E6A' },
                    { label: 'Total Impressions', value: `${(stats.totalImpressions / 1000).toFixed(0)}K`, sub: 'This month', color: '#1976D2' },
                    { label: 'Click-Through Rate', value: `${stats.ctr}%`, sub: `${stats.totalClicks.toLocaleString()} clicks`, color: '#FF6B35' },
                    { label: 'Revenue', value: `$${(stats.totalSpent / 1000).toFixed(1)}K`, sub: `of $${(stats.totalBudget / 1000).toFixed(0)}K budget`, color: '#2E7D32' },
                ].map((s, i) => (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                        <Card sx={{ borderRadius: 3 }}>
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: s.color }}>{s.value}</Typography>
                                <Typography variant="caption" color="text.secondary">{s.sub}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            
            {/* Controls */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 3, height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Visibility /> Page Controls
                            </Typography>
                            {(Object.keys(settings.pageSettings) as AdPage[]).map(page => (
                                <Box key={page} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f0f0f0' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {pageIcons[page]}
                                        <Typography variant="body2">{pageLabels[page]}</Typography>
                                    </Box>
                                    <Switch checked={settings.pageSettings[page]} onChange={() => togglePageSetting(page)} disabled={!globalAdsEnabled}
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#2D6E6A' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2D6E6A' } }} />
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 3, height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Settings /> Tier Controls
                            </Typography>
                            {(Object.keys(settings.tierSettings) as AdTier[]).map(tier => (
                                <Box key={tier} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip label={tier} size="small" sx={{ bgcolor: tierColors[tier], color: tier === 'premium' ? '#333' : '#fff', fontWeight: 600, textTransform: 'capitalize' }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {campaigns.filter(c => c.tier === tier && c.status === 'active').length} active
                                        </Typography>
                                    </Box>
                                    <Switch checked={settings.tierSettings[tier]} onChange={() => toggleTierSetting(tier)} disabled={!globalAdsEnabled}
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#2D6E6A' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2D6E6A' } }} />
                                </Box>
                            ))}
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Rotation Intervals</Typography>
                            {(Object.keys(settings.rotationIntervals) as AdTier[]).map(tier => (
                                <Box key={tier} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{tier}</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{settings.rotationIntervals[tier]}s</Typography>
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );

    // Campaigns Tab
    const renderCampaigns = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <TextField placeholder="Search campaigns..." size="small" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
                    sx={{ width: 300, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingCampaign(null); setCampaignDialogOpen(true); }}
                    sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#009688' } }}>
                    New Campaign
                </Button>
            </Box>
            
            <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            {['Campaign', 'Partner', 'Tier', 'Pages', 'Status', 'Performance', 'Actions'].map(h => (
                                <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredCampaigns.map(campaign => {
                            const partner = getPartnerById(campaign.partnerId);
                            const ctr = campaign.impressions > 0 ? ((campaign.clicks / campaign.impressions) * 100).toFixed(2) : '0.00';
                            return (
                                <TableRow key={campaign.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{campaign.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{campaign.startDate} - {campaign.endDate}</Typography>
                                    </TableCell>
                                    <TableCell>{partner?.name || 'Unknown'}</TableCell>
                                    <TableCell>
                                        <Chip label={campaign.tier} size="small" sx={{ bgcolor: tierColors[campaign.tier], color: campaign.tier === 'premium' ? '#333' : '#fff', fontWeight: 600, textTransform: 'capitalize' }} />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {campaign.pages.slice(0, 3).map(page => (
                                                <Tooltip key={page} title={pageLabels[page]}><Box sx={{ color: '#666' }}>{pageIcons[page]}</Box></Tooltip>
                                            ))}
                                            {campaign.pages.length > 3 && <Typography variant="caption" color="text.secondary">+{campaign.pages.length - 3}</Typography>}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={campaign.status} size="small" icon={campaign.status === 'active' ? <CheckCircle /> : <Pause />}
                                            sx={{ bgcolor: campaign.status === 'active' ? '#E8F5E9' : '#FFF3E0', color: campaign.status === 'active' ? '#2E7D32' : '#E65100', fontWeight: 600, textTransform: 'capitalize', '& .MuiChip-icon': { color: 'inherit', fontSize: 14 } }} />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{campaign.impressions.toLocaleString()} imp</Typography>
                                        <Typography variant="caption" color="text.secondary">{ctr}% CTR</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title={campaign.status === 'active' ? 'Pause' : 'Activate'}>
                                                <IconButton size="small" onClick={() => toggleCampaignStatus(campaign.id)} sx={{ color: campaign.status === 'active' ? '#E65100' : '#2E7D32' }}>
                                                    {campaign.status === 'active' ? <Pause /> : <PlayArrow />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditingCampaign(campaign); setCampaignDialogOpen(true); }}><Edit /></IconButton></Tooltip>
                                            <Tooltip title="Delete"><IconButton size="small" sx={{ color: '#d32f2f' }}><Delete /></IconButton></Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    // Partners Tab
    const renderPartners = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{partners.length} Partners</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingPartner(null); setPartnerDialogOpen(true); }}
                    sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#009688' } }}>Add Partner</Button>
            </Box>
            <Grid container spacing={3}>
                {partners.map(partner => {
                    const partnerCampaigns = campaigns.filter(c => c.partnerId === partner.id);
                    const activeCampaigns = partnerCampaigns.filter(c => c.status === 'active');
                    return (
                        <Grid item xs={12} sm={6} md={4} key={partner.id}>
                            <Card sx={{ borderRadius: 3, height: '100%' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Avatar sx={{ width: 48, height: 48, bgcolor: '#f0f0f0' }}><Business sx={{ color: '#666' }} /></Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{partner.name}</Typography>
                                            <Chip label={partner.status} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: partner.status === 'active' ? '#E8F5E9' : '#FFF3E0', color: partner.status === 'active' ? '#2E7D32' : '#E65100', textTransform: 'capitalize' }} />
                                        </Box>
                                        <Switch checked={partner.status === 'active'} onChange={() => togglePartnerStatus(partner.id)} size="small" />
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Campaigns</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{activeCampaigns.length} / {partnerCampaigns.length}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Contract</Typography>
                                        <Typography variant="body2">{partner.contractStart.split('-')[0]} - {partner.contractEnd.split('-')[0]}</Typography>
                                    </Box>
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                        <Button size="small" variant="outlined" onClick={() => { setEditingPartner(partner); setPartnerDialogOpen(true); }} sx={{ flex: 1, borderRadius: 2, textTransform: 'none' }}>Edit</Button>
                                        <Button size="small" variant="outlined" sx={{ flex: 1, borderRadius: 2, textTransform: 'none' }}>View Ads</Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );

    // Analytics Tab
    const renderAnalytics = () => (
        <Box>
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>Analytics dashboard with detailed charts coming soon.</Alert>
            <Card sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Top Performing Campaigns</Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead><TableRow>{['Campaign', 'Impressions', 'Clicks', 'CTR', 'Spent'].map(h => <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>)}</TableRow></TableHead>
                            <TableBody>
                                {[...campaigns].sort((a, b) => b.clicks - a.clicks).slice(0, 5).map(c => (
                                    <TableRow key={c.id}>
                                        <TableCell>{c.name}</TableCell>
                                        <TableCell>{c.impressions.toLocaleString()}</TableCell>
                                        <TableCell>{c.clicks.toLocaleString()}</TableCell>
                                        <TableCell>{c.impressions > 0 ? ((c.clicks / c.impressions) * 100).toFixed(2) : '0.00'}%</TableCell>
                                        <TableCell>${c.spent?.toLocaleString() || 0}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );

    // Audit Tab
    const renderAuditLog = () => (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table>
                <TableHead><TableRow sx={{ bgcolor: '#f5f5f5' }}>{['Timestamp', 'User', 'Action', 'Entity', 'Changes'].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>
                    {auditLog.map(log => (
                        <TableRow key={log.id} hover>
                            <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                            <TableCell>{log.userName}</TableCell>
                            <TableCell>
                                <Chip label={log.action} size="small" sx={{ bgcolor: log.action === 'enable' ? '#E8F5E9' : log.action === 'disable' || log.action === 'delete' ? '#FFEBEE' : '#E3F2FD', color: log.action === 'enable' ? '#2E7D32' : log.action === 'disable' || log.action === 'delete' ? '#C62828' : '#1565C0', fontWeight: 600, textTransform: 'capitalize' }} />
                            </TableCell>
                            <TableCell sx={{ textTransform: 'capitalize' }}>{log.entityType} ({log.entityId})</TableCell>
                            <TableCell>{log.changes.map((c, i) => <Typography key={i} variant="caption" display="block">{c.field}: {JSON.stringify(c.oldValue)} → {JSON.stringify(c.newValue)}</Typography>)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>Advertisements</Typography>
                    <Typography variant="body2" color="text.secondary">Manage ad campaigns, partners, and settings</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip icon={globalAdsEnabled ? <CheckCircle /> : <Cancel />} label={globalAdsEnabled ? 'Ads Enabled' : 'Ads Disabled'}
                        sx={{ bgcolor: globalAdsEnabled ? '#E8F5E9' : '#FFEBEE', color: globalAdsEnabled ? '#2E7D32' : '#C62828', fontWeight: 600, '& .MuiChip-icon': { color: 'inherit' } }} />
                    <Button variant="outlined" startIcon={<Download />} sx={{ borderRadius: 2, textTransform: 'none' }}>Export Report</Button>
                </Box>
            </Box>
            
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, minWidth: 'auto', px: 3 }, '& .Mui-selected': { color: '#2D6E6A !important' }, '& .MuiTabs-indicator': { backgroundColor: '#2D6E6A' } }}>
                <Tab value="overview" label="Overview" icon={<TrendingUp />} iconPosition="start" />
                <Tab value="campaigns" label="Campaigns" icon={<Campaign />} iconPosition="start" />
                <Tab value="partners" label="Partners" icon={<Business />} iconPosition="start" />
                <Tab value="analytics" label="Analytics" icon={<TrendingUp />} iconPosition="start" />
                <Tab value="audit" label="Audit Log" icon={<History />} iconPosition="start" />
            </Tabs>
            
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'campaigns' && renderCampaigns()}
            {activeTab === 'partners' && renderPartners()}
            {activeTab === 'analytics' && renderAnalytics()}
            {activeTab === 'audit' && renderAuditLog()}
            
            {/* Campaign Dialog */}
            <Dialog open={campaignDialogOpen} onClose={() => setCampaignDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>{editingCampaign ? 'Edit Campaign' : 'New Campaign'}</DialogTitle>
                <DialogContent><Alert severity="info" sx={{ mt: 1 }}>Campaign form coming in next iteration.</Alert></DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setCampaignDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" sx={{ bgcolor: '#2D6E6A', textTransform: 'none', '&:hover': { bgcolor: '#009688' } }}>{editingCampaign ? 'Save' : 'Create'}</Button>
                </DialogActions>
            </Dialog>
            
            {/* Partner Dialog */}
            <Dialog open={partnerDialogOpen} onClose={() => setPartnerDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>{editingPartner ? 'Edit Partner' : 'Add Partner'}</DialogTitle>
                <DialogContent><Alert severity="info" sx={{ mt: 1 }}>Partner form coming in next iteration.</Alert></DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setPartnerDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" sx={{ bgcolor: '#2D6E6A', textTransform: 'none', '&:hover': { bgcolor: '#009688' } }}>{editingPartner ? 'Save' : 'Add'}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminAdsPage;
